import EventEmitter from 'eventemitter3';
import Game from '../../Game';
import { sleep, shareAsyncAction } from '../async';
import IResourceDriver, { ResourceID, AtlasKey, ResourceURL, IQueueItem, FramesHash } from './IResourceDriver';
import { report } from '../../report';

export enum ResourceEvent {
    PROGRESS = 'resource_progress',
}

export interface IResourceDriverManagerOptions {
    autoStart?: boolean; // Start the loading process as soon as a resource is added.
    concurrency?: number; // The number of resources to load concurrently.
    maxAttempts?: number; // Max number of attempts to load a specific resource before giving up
}

export interface IResourceLoadingInfo {
    url: ResourceURL;
    attempts: number;
}

export default class ResourceDriverManager extends EventEmitter {
    // options
    private readonly autoStart: boolean;
    private readonly concurrency: number;
    private readonly maxAttempts: number;

    /**
     * Stores url and attempts number per resource ID,
     * from the moment it is added/scheduled until it's loaded or still failed after all retries
     */
    private readonly loadingInfoPerResourceID = new Map<ResourceID, IResourceLoadingInfo>();
    /**
     * Stores resource ID and url pairs,
     * from the moment they are added until they are started loading
     */
    private toStartLoading = new Array<IQueueItem>();

    private readonly loader: IResourceDriver;

    constructor(
        onResourceLoaded: (resourceIDOrAtlasKey: ResourceID | AtlasKey, loadedData: unknown) => void,
        options: IResourceDriverManagerOptions = {}
    ) {
        super();

        this.autoStart = Boolean(options.autoStart);
        this.concurrency = options.concurrency || Infinity;
        this.maxAttempts = options.maxAttempts || Infinity;

        this.loader = new Game.config.resourceDriver(
            (progress: number, resource?: PIXI.loaders.Resource) => {
                this.emit(ResourceEvent.PROGRESS, progress, resource);
            },
            (resourceIDOrAtlasKey, loadedData) => {
                this.forgetResource(resourceIDOrAtlasKey);
                onResourceLoaded(resourceIDOrAtlasKey, loadedData);
            },
            this.failToLoad
        );
    }

    public isLoading(resourceID: ResourceID): boolean {
        return this.loadingInfoPerResourceID.has(resourceID);
    }

    /**
     * Queue a resource for loading. Start loading resources by calling `load()`.
     * WARNING: this function expectes that resource with given ID wasn't already added
     */
    public add(resourceID: ResourceID, path: ResourceURL): void {
        this.loadingInfoPerResourceID.set(resourceID, {
            url: path,
            attempts: 1,
        });
        this.toStartLoading.push({ resourceID, path });

        if (this.autoStart) this.load();
    }

    /**
     * Load resources in the queue.
     */
    public readonly load = shareAsyncAction(() => this.doLoad());

    public loadFullQualityAtlases(): Promise<FramesHash> {
        return this.loader.loadFullQualityAtlases();
    }

    private async doLoad() {
        let attempt = -1;
        do {
            attempt += 1;

            this.updateToStartLoading();

            // Also covers resources that have been queued in the meanwhile
            while (this.toStartLoading.length > 0) {
                const chunkToLoad = this.toStartLoading.splice(
                    0,
                    Math.min(this.toStartLoading.length, this.concurrency)
                );
                await this.loader.load(chunkToLoad);

                this.updateToStartLoading();
            }

            // Whatever remains here have failed to be loade
            if (this.loadingInfoPerResourceID.size > 0) {
                // Wait before retrying (e.g. to counter error HTTP 429 "Too many requests")
                await sleep(Math.min(1000 + attempt * 500, 10000));

                this.updateToStartLoading();

                // Repopulate `toStartLoading`
                for (const [resourceID, loadingInfo] of this.loadingInfoPerResourceID) {
                    if (this.toStartLoading.some(entry => entry.resourceID === resourceID)) {
                        continue;
                    }
                    this.toStartLoading.push({
                        resourceID: resourceID,
                        path: loadingInfo.url,
                    });
                }
            }
        } while (this.toStartLoading.length > 0);
    }

    /** Remove entries that aren't in `loadingInfoPerResourceID` and ensure `path` is up to date */
    private updateToStartLoading() {
        this.toStartLoading = this.toStartLoading
            .map(entry => {
                const loadingInfo = this.loadingInfoPerResourceID.get(entry.resourceID);
                if (!loadingInfo) return null;
                if (entry.path !== loadingInfo.url) {
                    report('URL of resource changed', {
                        once: true,
                        sampleRate: 0.1,
                        tags: {
                            resourceID: entry.resourceID,
                            oldUrl: entry.path,
                            newUrl: loadingInfo.url,
                        },
                    });
                    return {
                        ...entry,
                        path: loadingInfo.url,
                    };
                }
                return entry;
            })
            .filter(Boolean);
    }

    /**
     * Stop the loading process. It's stops only the resources that still are in the queue.
     */
    public stop(): void {
        this.forgetAllPendingResources();
    }

    public informResourceCached(resourceID: ResourceID): void {
        this.forgetResource(resourceID);
    }

    private forgetAllPendingResources() {
        this.toStartLoading.forEach(item => this.loadingInfoPerResourceID.delete(item.resourceID));
        this.toStartLoading = [];
    }

    private forgetResource(resourceIDOrAtlasKey: ResourceID | AtlasKey) {
        this.loadingInfoPerResourceID.delete(resourceIDOrAtlasKey);
        this.toStartLoading = this.toStartLoading.filter(entry => entry.resourceID !== resourceIDOrAtlasKey);
    }

    /**
     * When a resouce fails in the load process check if it have to try to load again.
     * @param resourceID
     */
    private readonly failToLoad = (resourceID: ResourceID, error: unknown) => {
        const loadingInfo = this.loadingInfoPerResourceID.get(resourceID);
        if (!loadingInfo) return;
        if (loadingInfo.attempts < this.maxAttempts) {
            loadingInfo.attempts += 1;
        } else {
            report('Giving up on resource after maxAttempts reached', {
                error,
                once: true,
                sampleRate: 0.1,
                tags: {
                    resourceID,
                    attempts: loadingInfo.attempts,
                },
                fingerprint: 'failedToLoadAsset',
            });
            this.forgetResource(resourceID);
        }
    };
}
